import type {
  SprintMissionPoolSchema,
  MissionProgressSchema,
  PublicCampaignSchema,
} from "@/types";
import type { Mission } from "../missions/types";

/**
 * The assembled Sprint Mission view — `campaigns` ⋈ `mission_progress` ⋈
 * `sprint_mission_pool`.
 *
 * Composed from the generated schemas rather than re-declared (CAMPAIGN-ARCHITECTURE.md,
 * "Rely on the generated backend types"), so a backend change surfaces here as a type error.
 *
 * The subtype guarantees exactly one mission with exactly one prize and no task prizes
 * (enforced server-side in #3865), which is why this carries a single `mission` rather than the
 * standard mission campaign's ladder array.
 */
export interface SprintMission {
  campaign: PublicCampaignSchema;
  minimum_bet: { value: number; currency: string; display?: string };
  /** The one mission. `null` only in the window before its structural data arrives. */
  mission: Mission | null;
  /** Matches `toGameNames`' return: "ALL" is a distinct case from a list. */
  eligible_games: string[] | "ALL";
  eligible_games_count?: number;

  /** The shared pool facts: the counter and the recent-winners feed. Absent until the pool
   *  stream's first push. */
  pool: Pick<
    SprintMissionPoolSchema,
    | "prize_pool_size"
    | "prizes_remaining"
    | "exhausted"
    | "recent_winners"
    | "payout_timing"
  > | null;
}

/** One row of the recent-winners feed: the platform's already-masked name, the slot, when. */
export type SprintMissionWinner =
  SprintMissionPoolSchema["recent_winners"][number];

/** The wire caps the feed at this many rows; "N in the last hour" caps as "20+". */
export const RECENT_WINNERS_LIMIT = 20;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** The feed, newest first; `[]` before the pool has pushed or before anybody has won. */
export function recentWinners(lm: SprintMission): SprintMissionWinner[] {
  return lm.pool?.recent_winners ?? [];
}

/**
 * How many recent winners won inside the last hour, derived client-side because the pool stream
 * has no interval. `plus` is true when every row of the capped feed qualifies: the true number is
 * then at least the cap, and the copy says "20+".
 */
export function winnersInLastHour(
  lm: SprintMission,
  now: number = Date.now()
): { count: number; plus: boolean } {
  const count = recentWinners(lm).filter((winner) => {
    const wonAt = Date.parse(winner.won_at);
    return Number.isFinite(wonAt) && now - wonAt <= HOUR_MS;
  }).length;
  return { count, plus: count >= RECENT_WINNERS_LIMIT };
}

// The relative-time bucketing moved to `@/utils/relative-time` when the latest-wins card became
// type-agnostic; re-exported so existing imports keep resolving.
export { relativeTimeParts, type RelativeTimeKey } from "@/utils/relative-time";

export type ShortCountdownUnit = "days" | "hours" | "minutes";

/**
 * The compact countdown the feed's urgency line uses ("38m", "4h 32m", "17d 4h"), as the
 * leading unit plus the numbers; the caller renders it through the `sprintMission.countdown*`
 * templates so the unit letters are translated, not hardcoded.
 */
export function shortCountdownParts(ms: number): {
  unit: ShortCountdownUnit;
  days: number;
  hours: number;
  minutes: number;
} {
  const total = Math.max(0, ms);
  const days = Math.floor(total / DAY_MS);
  const hours = Math.floor((total % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((total % HOUR_MS) / MINUTE_MS);
  const unit: ShortCountdownUnit =
    days > 0 ? "days" : hours > 0 ? "hours" : "minutes";
  return { unit, days, hours, minutes };
}

/** Per-player progress rows for one campaign, as the pool assemble consumes them. */
export type SprintMissionProgress = MissionProgressSchema;

/**
 * Slots left, or `null` while the pool stream has not yet pushed.
 *
 * `null` is deliberately distinct from `0`: "we don't know yet" must not render as "all gone",
 * which is the most consequential thing this campaign type can say to a player.
 */
export function prizesRemaining(lm: SprintMission): number | null {
  return lm.pool ? lm.pool.prizes_remaining : null;
}

/**
 * True when every slot is taken.
 *
 * Reads the backend's `exhausted` rather than deriving `prizes_remaining === 0` — the backend
 * sends it precisely so the two surfaces cannot disagree — but also treats a finished campaign
 * with no slots left as exhausted, since a campaign that ended on its date with an empty pool
 * is the same story to a player.
 */
export function isExhausted(lm: SprintMission): boolean {
  if (!lm.pool) return false;
  return lm.pool.exhausted || lm.pool.prizes_remaining === 0;
}

/**
 * How loudly the counter should present itself.
 *
 * Frontend policy, deliberately (`missions/DESIGN.md` §4 — the backend exposes the two numbers
 * and no threshold signal, so each surface can treat scarcity differently and retuning needs no
 * deploy).
 *
 * - `calm` — plenty left; the counter informs.
 * - `urgent` — few enough that it changes a player's decision to keep going.
 * - `gone` — none left.
 */
export type PoolUrgency = "calm" | "urgent" | "gone";

/**
 * Two tiers, not three, and three independent arms: an absolute floor, a ratio, and time.
 *
 * A ratio alone misreads small pools, which are the common case here: 1 left of 3 is 33% and
 * about as urgent as this campaign type gets, while 25 left of 100 is the same ratio and is not
 * urgent at all. The absolute arm is what makes the last few slots read as scarce regardless of
 * how big the pool started — and it means a pool of five or fewer is urgent from its first
 * minute, which is the intended reading for tiny pools. The time arm covers the other way a
 * player can miss out: plenty of slots left but the run itself about to close (#3958).
 */
// Twin of the overlay's `CRUCIAL_*` in `overlay/src/v2.0/utils/sprintMission.js` (two bundles, so
// the numbers are copied, not shared): retune both together, and keep `msUntilEnd`,
// `relativeTimeParts`, `winnersInLastHour` and `sprintMissionPhase` in step with their
// namesakes there.
export const URGENT_ABSOLUTE = 5;
export const URGENT_RATIO = 0.1;
export const URGENT_TIME_MS = 60 * 60 * 1000;

/**
 * Milliseconds until the run closes by date, or `null` when the end is not a parseable date.
 * Negative once the end has passed.
 */
export function msUntilEnd(lm: SprintMission, now = Date.now()): number | null {
  const end = Date.parse(lm.campaign.end);
  return Number.isFinite(end) ? end - now : null;
}

/**
 * `now` is a parameter, not read inside, so the rule stays pure: the store's assemble step must
 * never depend on the clock (it would defeat `listEqual` and re-render on every tick). Callers
 * that need the time arm to stay current use `useSprintMissionPhase`, which owns the timer.
 */
export function poolUrgency(
  lm: SprintMission,
  now = Date.now()
): PoolUrgency | null {
  // `null` propagates "we don't know yet" rather than collapsing to `calm`, so a counter that
  // has not loaded renders nothing instead of a confident-looking value.
  if (!lm.pool) return null;
  if (isExhausted(lm)) return "gone";

  const { prizes_remaining: remaining, prize_pool_size: size } = lm.pool;
  const ratio = size > 0 ? remaining / size : 1;
  const msLeft = msUntilEnd(lm, now);
  const closingSoon = msLeft !== null && msLeft <= URGENT_TIME_MS;
  return remaining <= URGENT_ABSOLUTE || ratio <= URGENT_RATIO || closingSoon
    ? "urgent"
    : "calm";
}

/**
 * The player holds a slot.
 *
 * No backend flag is needed: a Sprint Mission's mission-completed event is only ever written
 * after the slot claim succeeded (`missions/models.py`, `complete_mission_for_player` — "no
 * slot: no payout, no completion events"), and `is_completed` is derived from that event. A
 * player who lost the race gets a campaign-scoped "pool missed" event instead, never a mission
 * one. So this is true by construction, not by convention.
 */
export function hasWon(lm: SprintMission): boolean {
  return lm.mission?.isCompleted === true;
}

/**
 * The prize lands when the run ends rather than on the claim (an operator setting). `false`
 * while the pool has not pushed: the strip that reads this must not promise anything yet.
 */
export function holdsPayoutUntilEnd(lm: SprintMission): boolean {
  return lm.pool?.payout_timing === "end_of_campaign";
}

/** How many of the mission's tasks the player has completed. */
export function completedTaskCount(lm: SprintMission): number {
  return lm.mission?.tasks.filter((task) => task.isCompleted).length ?? 0;
}

/**
 * When the run ended by *date*, or `null` when the end is not knowable.
 *
 * A pool that ran dry ends the campaign early (the backend flips it to FINISHED and stamps
 * `exhausted_at` on the pool), but `campaign.end` is left untouched and `exhausted_at` is not on
 * the wire. Showing `campaign.end` in that case would print a date in the future for something
 * that is already over, so an exhausted run has no finish date until the backend exposes one.
 */
export function finishedAt(lm: SprintMission): Date | null {
  if (lm.campaign.status !== "FI" || isExhausted(lm)) return null;
  const end = Date.parse(lm.campaign.end);
  return Number.isFinite(end) ? new Date(end) : null;
}

/**
 * The one player-facing state machine every Sprint Mission surface renders from.
 *
 * - `loading` — the pool has not pushed yet; render nothing confident.
 * - `calm` — plenty left, the counter informs.
 * - `crucial` — few left, few percent left, or under an hour to go: the counter warns.
 * - `won` — this player holds a slot; the run is still going.
 * - `finished` — the run is over, by date or because the pool ran dry.
 *
 * Finished beats won: a winner of a finished run sees the finished layout, with their own
 * claim acknowledged inside it, rather than a live winner card for a run that no longer exists.
 */
export type SprintMissionPhase =
  | "loading"
  | "calm"
  | "crucial"
  | "won"
  | "finished";

export function sprintMissionPhase(
  lm: SprintMission,
  now = Date.now()
): SprintMissionPhase {
  // `exhausted` alone counts as finished: the status flip lands in the same backend transaction
  // but can reach a client in a later push, and a one-beat "0 left, in progress" frame is worse
  // than calling it early.
  if (lm.campaign.status === "FI" || isExhausted(lm)) return "finished";
  if (hasWon(lm)) return "won";
  const urgency = poolUrgency(lm, now);
  if (urgency === null) return "loading";
  return urgency === "urgent" ? "crucial" : "calm";
}
